import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Sparkles, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { businessInfo } from '../mock';

const Hero = () => {
  const navigate = useNavigate();
  const scrollToServices = () => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });

  // Masked line-by-line reveal for headline
  const line1 = 'Your Car.';
  const line2 = 'Your Vision.';
  const line3 = 'Perfected.';

  const lineVariants = {
    hidden: { y: '110%', opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: { delay: 0.2 + i * 0.15, duration: 0.9, ease: [0.25, 1, 0.5, 1] }
    })
  };

  return (
    <section
      id="hero"
      className="relative min-h-[72vh] flex items-center justify-center overflow-hidden bg-black pt-24 pb-14"
    >
      {/* Backdrop: car photo + heavy dark tint */}
      <div className="absolute inset-0">
        <motion.img
          src="/images/gallery12.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          initial={{ scale: 1.15, filter: 'brightness(0.3) saturate(1.2)' }}
          animate={{ scale: 1, filter: 'brightness(0.55) saturate(1.3)' }}
          transition={{ duration: 2.5, ease: [0.25, 1, 0.5, 1] }}
        />
        {/* Vignette */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/40"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-transparent to-transparent"></div>
      </div>

      {/* Grid + glow accents */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: 'linear-gradient(rgba(0,191,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,191,255,0.08) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
      }}></div>
      <div className="absolute top-1/4 -right-20 w-96 h-96 bg-cyan-500 rounded-full blur-[120px] opacity-20"></div>
      <div className="absolute bottom-1/4 -left-20 w-96 h-96 bg-blue-500 rounded-full blur-[120px] opacity-15"></div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-10">
        <div className="max-w-3xl">
          {/* Micro label */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-xl border border-cyan-400/20 rounded-full px-4 py-1.5 mb-5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
            <span className="text-xs uppercase tracking-widest text-cyan-300 font-bold">Sydney · Mobile Detailing</span>
          </motion.div>

          {/* Masked line-by-line reveal headline */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-[0.95] mb-5 tracking-tight">
            <span className="block overflow-hidden">
              <motion.span className="block" variants={lineVariants} initial="hidden" animate="visible" custom={0}>{line1}</motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span className="block" variants={lineVariants} initial="hidden" animate="visible" custom={1}>{line2}</motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span
                className="block bg-gradient-to-r from-cyan-300 via-cyan-400 to-blue-400 bg-clip-text text-transparent italic"
                variants={lineVariants} initial="hidden" animate="visible" custom={2}
              >
                {line3}
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="text-lg sm:text-xl text-white/70 max-w-xl mb-7 leading-relaxed"
          >
            Premium mobile detailing, window tinting and ceramic protection across Sydney. Enquire about paint correction, tinting and special services today.
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="flex flex-col sm:flex-row gap-3 mb-8"
          >
            <Button
              data-testid="hero-build-btn"
              onClick={() => navigate('/special-services')}
              size="lg"
              className="group relative bg-gradient-to-r from-cyan-400 to-blue-500 hover:from-cyan-300 hover:to-blue-400 text-slate-950 rounded-xl px-8 py-7 font-extrabold text-base shadow-2xl shadow-cyan-500/40 hover:shadow-cyan-400/60 transition-all duration-300 hover:scale-[1.02] overflow-hidden"
            >
              <span className="relative z-10 flex items-center">
                <Zap className="w-5 h-5 mr-2" />
                SPECIAL SERVICES
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </span>
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/40 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></span>
            </Button>
            <Button
              data-testid="hero-services-btn"
              onClick={scrollToServices}
              size="lg"
              variant="outline"
              className="border-2 border-white/15 bg-white/5 text-white hover:bg-white/10 hover:border-white/30 backdrop-blur-md rounded-xl px-8 py-7 font-bold text-base"
            >
              EXPLORE OUR SERVICES
            </Button>
          </motion.div>

          {/* Trust bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.9 }}
            className="flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-widest font-bold text-white/50"
          >
            <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-cyan-400" />Mobile Detailing</div>
            <div className="w-px h-4 bg-white/10"></div>
            <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-cyan-400" />Window Tinting</div>
            <div className="w-px h-4 bg-white/10"></div>
            <div className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-cyan-400" />Ceramic Coating</div>
          </motion.div>
        </div>
      </div>

      {/* Floating VIN-style spec card */}
      <motion.a
        href={`tel:${businessInfo.phone.replace(/\s/g, '')}`}
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="hidden xl:block absolute bottom-10 right-10 bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-colors group z-10"
      >
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold mb-1">Instant Line</div>
        <div className="text-xl font-extrabold text-white tracking-wider group-hover:text-cyan-300 transition-colors">
          <Phone className="w-4 h-4 inline mr-2 -mt-1" />
          {businessInfo.phone}
        </div>
      </motion.a>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-white/40"
      >
        <span className="text-[10px] uppercase tracking-widest">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/40 to-transparent"></div>
      </motion.div>
    </section>
  );
};

export default Hero;
